import type { Locale } from '../i18n';
import { create } from 'zustand';

import type { Sortierart } from '../domain/candidateSort';
import type { KommentarArt, KommentarArten } from '../domain/kommentararten';
import { naechsterOffener } from '../domain/naechster';
import { VOLLE_ANZEIGE, type BogenAnzeige, type BogenAnzeigeSchalter } from '../domain/bogenAnzeige';
import { LAGE_STILL, type KatalogLage } from '../domain/examCatalogue';
import { DATEIARTEN, LEERER_RUECKSTAND, type Rueckstandszahlen } from '../domain/rueckstand';
import { criterionKey, emptyAssessment, stationBeruehrt, wordKey, type Assessment } from '../domain/scoring';
import {
  emptyComment,
  type CommentMedia,
  type Exam,
  type InkStroke,
  type Candidate,
  type MediaKind,
  type Station,
  type Task,
  type TaskId,
} from '../domain/types';
import { flushNow, resetBaseline } from '../persistence/autosave';
import { candidateStatus, setCandidateStatus, updateClientSelection } from '../persistence/client';
import { appendLog } from '../persistence/examLog';
import { abgesendeteBoegen, type HydrationResult } from '../persistence/hydrate';
import {
  baueAbzugBeschriftung, captureBogen, bogenShotDeps, type Abzugsgrund,
} from '../features/bogen/bogenShot';
import { appendAppLog } from '../platform/appLog';
import { setGeraeteName } from '../platform/appVersion';
import { readExam } from '../persistence/readExam';
import { buildTaskIndex, loadAssessment, type TaskIndex } from '../persistence/repos';
import { saveSetting } from '../persistence/settings';
import {
  ausRelay,
  JE_PRUEFUNG,
  schreibeEinstellung,
  relayName,
  type Gemischt,
  type Quelle,
  type Schluessel,
  type Wert,
} from '../platform/einstellungen';
import { schreibeAppJePruefung } from '../persistence/pruefungsEinstellungen';
import { getStore } from '../persistence/store';
import type { Accent } from '../theme/palettes';
import { useToastStore } from '../ui/Toast';
/**
 * `Sheet` wohnt in `./sheet.ts`, nicht hier: `hydrate.ts` (Persistenzschicht) braucht den Typ
 * und `normalizeSheet` ebenso wie dieser Store, und läge beides hier, bräuchte `hydrate` diesen
 * Store — ein Ringschluss. Zwei Anweisungen, bewusst nicht eine einzelne
 * `export type { Sheet } from './sheet';`: ein reiner Re-Export bindet `Sheet` nicht lokal, der
 * Rest dieser Datei braucht den Namen aber selbst (`sheet: Sheet`, `as Sheet` unten) — deshalb
 * zusätzlich der eigene Import, und `export type { Sheet };` hält die Oberfläche unverändert.
 */
import { normalizeSheet, SHEET_KEY, stateFromStatus, type RelayStatus, type Sheet } from './sheet';

import { startzustand, type Startup } from './startup';
import { useTimerStore } from './useTimerStore';

export type { Sheet };

/**
 * Ein Store für die ganze App. Die Vorlage führt ihren Zustand in einem flachen Objekt;
 * das ist hier übernommen, weil fast jede Anzeige quer über mehrere dieser Felder liest
 * (Punktzahl hängt an Eingaben, Farben am Modus, Fortschritt an Kommentaren).
 */

/** Filterknöpfe über der Prüflingsliste. */
export type CandidateFilter = 'Alle' | 'Offen' | 'Bewertet';

/** Was nach dem Absenden geschieht. */
export interface AfterSubmit {
  /** Danach gleich den QR-Scanner öffnen statt der Liste. */
  scanner: boolean;
  /** Kurze Rückmeldung bei jedem Tipp auf eine Checklistenzeile. */
  haptics: boolean;
}

/** Eine abgesendete Bewertung. Der Bogen ist danach gesperrt, bleibt aber sichtbar. */
export interface SubmittedRecord {
  /** Zeitpunkt als `HH:MM`. */
  at: string;
  total: number;
  max: number;
}

/** Vorgabe der Aufnahmedauer: fünf Minuten, wie bisher im Videoplugin fest verdrahtet. */
export const VIDEO_VORGABE_SEKUNDEN = 300;

/**
 * Hält die Aufnahmedauer in einem brauchbaren Bereich.
 *
 * Unter 30 s lohnt keine Aufnahme; über 15 Minuten sprengt der Zusammenschnitt auf einem
 * iPad 6 Speicher und Geduld. Ein unlesbarer oder fehlender Wert fällt auf die Vorgabe
 * zurück — `Number(undefined)` ist `NaN`, und ein `NaN` als Obergrenze beendete jede
 * Aufnahme sofort.
 */
export function begrenzeVideoDauer(sekunden: number): number {
  if (!Number.isFinite(sekunden)) return VIDEO_VORGABE_SEKUNDEN;
  return Math.min(900, Math.max(30, Math.round(sekunden)));
}

/** Vorgabe der Aufbewahrung im Archiv: sechs Monate. */
export const ARCHIV_VORGABE_TAGE = 180;

/**
 * Hält die Aufbewahrungsfrist in einem sinnvollen Bereich.
 *
 * Nach unten 7 Tage: eine Frist von null hiesse „sofort löschen" und machte die zweite Stufe
 * sinnlos — wer das will, soll es nicht aus Versehen einstellen können. Nach oben fünf Jahre.
 * Ein unlesbarer Wert fällt auf die Vorgabe zurück; ein `NaN` als Frist löschte beim nächsten
 * Start jeden Ordner, weil jeder Vergleich damit falsch ist.
 */
export function begrenzeArchivTage(tage: number): number {
  if (!Number.isFinite(tage)) return ARCHIV_VORGABE_TAGE;
  return Math.min(1825, Math.max(7, Math.round(tage)));
}

/**
 * Woher das Erscheinungsbild kommt.
 *
 * ⚠️ **`system` heisst „wie das iPad"** — das ist keine fehlende Angabe, sondern die Vorgabe
 * (Entscheidung des Nutzers, 2026-09-17), und dasselbe Muster wie bei `language`. Bis zum
 * 2026-10-01 stand dafür `''`, für hell und dunkel `hell`/`dunkel` — seitdem Relays Werte. Ein blosser
 * Schalter könnte das nicht: seine Stellung „aus" wäre nicht von „das Gerät ist hell"
 * zu unterscheiden, und nach dem ersten Tippen folgte die App dem Gerät nie wieder.
 */
export type Thema = 'system' | 'light' | 'dark';

/**
 * Ob bei dieser Wahl dunkel gezeichnet wird.
 *
 * ⚠️ **`system` fragt das Gerät** — im WKWebView folgt `prefers-color-scheme` der
 * Einstellung von iOS. Ohne `matchMedia` (Testlauf in `node`) bleibt es hell: eine Attrappe,
 * die „dunkel" behauptet, wäre in einem Test die falsche Ausgangslage.
 */
export function istDunkel(thema: Thema): boolean {
  if (thema === 'dark') return true;
  if (thema === 'light') return false;
  return typeof window !== 'undefined' && typeof window.matchMedia === 'function'
    ? window.matchMedia('(prefers-color-scheme: dark)').matches
    : false;
}

interface AppState {
  // — Darstellung —
  /** Die **Wahl**: leer folgt dem iPad. Was daraus zu zeichnen ist, sagt `dark`. */
  thema: Thema;
  /** Die Vorgabesprache (`language`); was gezeigt wird, entscheidet `features/sprache.ts`. */
  sprache: 'system' | Locale;
  /** Darf der Prüfer die Sprache selbst wählen (`language_changeable`)? Sonst kein Wähler. */
  spracheWaehlbar: boolean;
  /** Ob gerade dunkel gezeichnet wird — aus `thema` und, bei leerer Wahl, aus dem Gerät. */
  dark: boolean;
  accent: Accent;
  /** Ob halbe Punkte vergeben werden dürfen. */
  halfPoints: boolean;
  /** Ob der Timer im Kopf sichtbar ist. */
  timerShow: boolean;
  /** Ob der Kommentar zur Station unten bei der Abgabe angeboten wird. */
  stationComments: boolean;
  /**
   * Obergrenze einer Videoaufnahme in Sekunden, über **alle** Segmente zusammen.
   *
   * Die eigene Kameraoberfläche pausiert und setzt fort; die Grenze gilt für die Summe, nicht
   * je Segment — sonst liesse sich eine beliebig lange Aufnahme aus lauter kurzen Stücken
   * zusammensetzen.
   */
  videoMaxSeconds: number;
  /**
   * Wie lange die Dateien einer gelöschten Prüfung im Archiv bleiben, in **Tagen**.
   *
   * Gelöscht wird eine Prüfung in zwei Stufen: ihre Datenbankzeilen sofort, ihre Aufnahmen
   * erst nach dieser Frist (`platform/archive.ts`). Bis dahin liegen sie unter
   * `old/<Löschdatum>/logs/…` und sind wiederherstellbar.
   */
  archivTage: number;
  /**
   * Der Helligkeitsrahmen, den die Aufsicht spannt — in Prozent.
   *
   * ⚠️ **Nur lesend, mit Absicht kein Setter.** Die Grenzen stehen in den iPad-Einstellungen
   * (`min_brightness`, `max_brightness`) und werden nicht in der App verstellt. Die Wahl des
   * Benutzers *innerhalb* der Grenzen ist dagegen gar keine Einstellung, sondern
   * Sitzungszustand — sie liegt im `sessionStorage` (`platform/helligkeit.ts`) und nicht hier.
   */
  helligkeitMin: number;
  helligkeitMax: number;
  /**
   * Der Name dieses iPads, in den Einstellungen der App vergeben.
   *
   * ⚠️ **Nicht `UIDevice.current.name`.** Der ist nur die Voreinstellung: ab iOS 16 liefert
   * Apple ihn fremden Apps nicht mehr, sondern gibt bloss das Modell zurück. Ein
   * Prüfungsgerät braucht aber einen wiedererkennbaren Namen („Prüfungs-iPad 3"), und der
   * gehört in jede Beglaubigung — also wird er hier gefragt und gespeichert.
   */
  deviceName: string;
  /**
   * Was beim letzten Blick auf den Server herauskam.
   *
   * Nur die **Zahlen**, nicht die Prüfungen: die Kopfzeile sagt, *dass* etwas ist, die
   * Prüfungsauswahl sagt, *bei welcher*. Gefüllt wird das allein von der Auswahl, solange sie
   * offen ist (sie fragt beim Öffnen und dann alle zehn Sekunden) — anderswo fragt niemand
   * nach, und ein Wert, den niemand auffrischt, bleibt eben stehen. Das ist gewollt: der
   * letzte bekannte Befund ist mehr wert als gar keiner.
   */
  katalogLage: KatalogLage;

  // — Ablage —
  /** Die geladene Prüfung. `null`, solange nicht hydriert ist. */
  exam: Exam | null;
  /** Brücke Aufgabe → ExamItem/ExamQuestion/ExamAnswer, für das Schreiben ins Protokoll. */
  taskIndex: TaskIndex;
  clientId: string;
  /** Übernimmt das Ergebnis von `hydrate()` in den Store; einmalig beim Start. */
  applyHydration: (result: HydrationResult) => void;

  // — Auswahl —
  examId: string;
  stationId: string;
  candidateId: string;
  examinerId: string;
  actorId: string;

  /**
   * Bewertungen je Prüfling. Beim Wechsel wird nicht zurückgesetzt, sondern umgeschaltet —
   * ein versehentlicher Wechsel darf keine Arbeit vernichten.
   */
  assessments: Record<string, Assessment>;

  /**
   * Wo der Startablauf steht. Nur bei `bereit` ist Bewerten möglich — und nur dann schreibt
   * die Sicherung (s. `autosave.ts`). Siehe `startup.ts` für den Automaten.
   */
  startup: Startup;

  // — Overlays —
  sheet: Sheet;
  /**
   * Beschriftung der Sperrscheibe während des Bogenabzugs; `null` heisst: kein Abzug läuft.
   * Siehe `features/bogen/CaptureOverlay.tsx`.
   */
  shotStamp: string | null;
  /**
   * Ob die Schlafscheibe liegt: nach fünf Minuten ohne Berührung legt sich ein fast
   * undurchsichtiges Feld über alles, und nur der Schlafknopf weckt wieder auf. Siehe
   * `features/schlaf/`.
   */
  schlaeft: boolean;
  /**
   * Ob gerade Ton oder Video aufnimmt.
   *
   * ⚠️ **Nur für die Schlafscheibe da.** Sie muss wissen, wann sie sich nicht über ein
   * laufendes Kamerabild legen darf; der Prüfer hält dabei minutenlang das iPad, ohne es
   * anzurühren. Die Aufnahme selbst braucht dieses Feld nicht — es ist eine Meldung nach
   * aussen, kein Betriebsmittel (`MediaTabs.tsx`, `platform/video.ts`).
   */
  medienAufnahme: boolean;
  /**
   * Ob ein Blatt offen ist, das den ganzen Bildschirm füllt **und unten eine eigene Steuerung
   * trägt** (heute: der Video-Annotationseditor).
   *
   * ⚠️ Der schwebende Schlafknopf steht mit `z-index: 61` über jedem Blatt; dort verdeckte er
   * erst die Texteingabe, dann das rechte Ende der Zeitleiste — und dort liegt das Videoende.
   * Wie `medienAufnahme` ist das eine **Meldung nach aussen**, kein Betriebsmittel: der Editor
   * braucht das Feld selbst nicht (`schlaf/schlafecke.ts::zeigtSchwebendenKnopf`).
   */
  vollbildEditor: boolean;
  /** Prüfling, zu dem gewechselt werden soll; die Rückfrage liegt über der Liste. */
  switchTarget: string | null;
  candidateFilter: CandidateFilter;
  candidateSearch: string;
  /**
   * Wonach die Prüflingsliste geordnet ist. Gilt für die Sitzung, nicht über den Neustart
   * hinaus: welche Ordnung gerade hilft, hängt daran, was der Prüfer in der Hand hält — eine
   * Namensliste, einen Laufzettel mit Prüfungsnummern oder einen Ausweis.
   */
  candidateSort: Sortierart;

  afterSubmit: AfterSubmit;
  /**
   * Welche Kommentararten das Blatt anbietet (`*_comments`).
   *
   * Ein Bündel statt fünf Feldern — wie `afterSubmit`. Was daraus folgt, entscheidet
   * `domain/kommentararten.ts`, nicht der Store.
   */
  kommentarArten: KommentarArten;
  /**
   * Was der Bogen zeigt und erlaubt: Farben, Punkte, Summe, Änderbarkeit, Schauspieler
   * (`domain/bogenAnzeige.ts`, seit 2026-10-02).
   */
  bogenAnzeige: BogenAnzeige;
  /** Darf sich die App ins Querformat drehen? (`landscape_mode`, `platform/ausrichtung.ts`) */
  querformat: boolean;
  /**
   * Gehen Bewertungen und Dateien von selbst hinaus? (`auto_upload`, nur iPad-Einstellungen)
   *
   * Gepuffert wird in jedem Fall, und zwar sofort — `submit_buffer` und
   * `submit_require_complete` sind am 2026-10-02 entfallen (Entscheidung des Nutzers). Aus
   * heisst: nur „Jetzt übertragen“ sendet (`platform/anstoss.ts`).
   */
  autoUpload: boolean;
  /** Gehen Fehlerberichte an GlitchTip? (`error_reporting`, `platform/fehlerberichte.ts`) */
  fehlerberichte: boolean;
  /**
   * Die zwei Schalter der Fotos-Kopie, wie sie nach der Rangfolge gelten
   * (`platform/fotos.ts`).
   *
   * ⚠️ **Sie stehen hier, obwohl keine Oberfläche sie zeigt.** Die Ablage darf nichts aus dem
   * Store holen; über diesen Umweg gilt für die Kopie dieselbe Rangfolge wie für alles andere
   * — vor allem gilt ein gescannter QR-Code. Angemeldet wird der Zugriff in `main.tsx`.
   */
  fotosKopie: boolean;
  medienBeschriftung: boolean;
  /**
   * Was noch hinaus muss — die Zahlen hinter der Pille in der Kopfzeile
   * (`features/header/Rueckstandspille.tsx`).
   *
   * ⚠️ **Gezählt wird nicht hier.** Der Store nimmt nur entgegen: `domain/rueckstand.ts`
   * rechnet, `server/rueckstand.ts` zählt, `features/header/rueckstandMelden.ts` trägt es ein.
   * Der Store darf nichts aus `server/` holen — sonst hinge die Ablage an der Oberfläche
   * (dieselbe Regel wie bei `platform/fotos.ts`).
   */
  rueckstand: Rueckstandszahlen;
  /** Läuft gerade ein Übertragungsdurchgang? */
  rueckstandSendet: boolean;
  /**
   * Die letzte Meldung der Übertragung, wie sie im Aufklapper steht.
   *
   * Nur die **letzte**: der Prüfer braucht keine Geschichte, sondern die Auskunft, woran es
   * gerade hängt. Das ganze Protokoll steht im Dateilog.
   */
  rueckstandMeldung: { zeit: string; text: string } | null;
  /**
   * Nimmt eine Videoaufnahme Ton auf? Vorgabe **an** (`video_audio`).
   *
   * Ausschalten spart Dateigrösse — und damit Übertragungszeit am Prüfungstag. Es ist eine
   * Entscheidung der Einrichtung; der Prüfer kann sie in der Kameraoberfläche vor Beginn
   * einer Aufnahme noch umstellen (`VideoCaptureViewController.swift`).
   */
  videoTon: boolean;
  /**
   * Gegen welchen Zeitserver die Uhr gehalten wird (`ntp_server`).
   *
   * **Leer heisst: gar nicht.** Ein Haus, dessen Netz UDP 123 sperrt oder das keinen fremden
   * Server fragen will, trägt nichts ein — dann bleibt `timeSource: 'system'`, und niemand
   * bekommt eine Meldung darüber (s. `platform/clockCheck.ts`).
   */
  ntpServer: string;
  /**
   * Woher jede Einstellung stammt (`platform/einstellungen.ts`). Die Oberfläche sperrt damit
   * die Zeilen, die eine Geräteverwaltung vorgibt — sonst ändert der Prüfer einen Wert, er
   * springt beim nächsten Lesen zurück, und die App sieht kaputt aus.
   */
  quellen: Partial<Record<Schluessel, Quelle>>;
  /**
   * Das Passwort aus den iPad-Einstellungen, das den Einstellungsdialog schützt. Leer heisst:
   * keine Sperre (`features/sheets/einstellungenSperre.ts`).
   */
  einstellungenPasswort: string;
  /**
   * Ist die Sperre für **diese Sitzung** gefallen?
   *
   * ⚠️ Wird beim Einschlafen und beim Verlassen der App zurückgesetzt: Ein iPad, das
   * entsperrt auf dem Tisch liegen bleibt, ist genau der Fall, den die Sperre verhindern soll.
   */
  einstellungenFrei: boolean;
  /**
   * Die PIN, die den Prüfungsclient am Rechner beenden darf (`exit_pin`).
   *
   * ⚠️ **Leer heisst hier nicht offen** — anders als beim Einstellungspasswort. Dann zählt
   * allein der Notfallausweg, s. `features/beenden/beendenSperre.ts`.
   */
  beendenPin: string;
  /**
   * Steht die Abfrage vor dem Beenden offen?
   *
   * ⚠️ **Kein `Sheet`.** Sie muss über allem aufgehen können, auch über einem offenen Blatt:
   * ⌘Q kommt, wann es kommt, und der Bogen liegt dann vielleicht gerade unter den
   * Einstellungen. Ein Blatt verdrängte das andere — und der Prüfer fände nach dem Beenden
   * einen anderen Bildschirm vor als den, den er verlassen hat.
   */
  beendenOffen: boolean;
  /** Abgesendete Bewertungen je Prüfling. */
  submitted: Record<string, SubmittedRecord>;

  // — Aktionen —
  setzeThema: (thema: Thema) => void;
  /** Meldet die Systemeinstellung des Geräts; wirkt nur, solange die Wahl leer ist. */
  geraetIstDunkel: (dunkel: boolean) => void;
  setAccent: (accent: Accent) => void;
  toggleHalfPoints: () => void;
  setTimerShow: (show: boolean) => void;
  setVideoMaxSeconds: (sekunden: number) => void;
  setArchivTage: (tage: number) => void;
  /** Übernimmt den gemischten Stand aus MDM, iPad-Einstellungen und Ablage. */
  uebernehmeEinstellungen: (gemischt: Gemischt) => void;
  /** Die Sperre vor dem Einstellungsdialog öffnen bzw. wieder schliessen. */
  setEinstellungenFrei: (frei: boolean) => void;
  /** Die Abfrage vor dem Beenden öffnen bzw. wegnehmen. */
  setBeendenOffen: (offen: boolean) => void;
  setDeviceName: (name: string) => void;
  /** Setzt das Passwort vor den Einstellungen aus der App heraus; `''` entfernt es. */
  setEinstellungenPasswort: (passwort: string) => void;
  setStationComments: (an: boolean) => void;
  setFotosKopie: (an: boolean) => void;
  setMedienBeschriftung: (an: boolean) => void;
  setVideoTon: (an: boolean) => void;
  setzeRueckstand: (zahlen: Rueckstandszahlen) => void;
  setzeRueckstandSendet: (an: boolean) => void;
  merkeRueckstandsmeldung: (text: string) => void;
  setzeKatalogLage: (lage: KatalogLage) => void;

  selectExam: (examId: string) => void;
  /**
   * Liest die **ausgewählte** Prüfung neu aus der Ablage, nachdem sie unter ihr ersetzt wurde
   * (`server/nachladen.ts`). Station, Prüfling, Prüfer und Schauspieler bleiben gewählt, soweit
   * es sie in der neuen Fassung noch gibt. Andere Prüfungen: nichts.
   */
  ladeInhaltNeu: (examId: string) => Promise<void>;
  /**
   * ⚠️ **Seit dem Wechsel-Abzug `async`** (Vorgabe des Nutzers, 2026-09-28). Der Abzug der
   * verlassenen Station muss fertig sein, **bevor** `stationId` wechselt — sonst zeigte das
   * Beweisbild schon die Aufgaben der neuen. Die Aufrufer in `SetupSheet.tsx` feuern und
   * vergessen; solange der Abzug läuft, liegt die Sperrscheibe über dem Bogen.
   */
  selectStation: (stationId: string) => Promise<void>;
  selectExaminer: (examinerId: string) => void;
  selectActor: (actorId: string) => void;

  /**
   * Eine Prüfung ist vom Gerät verschwunden. Räumt auf, was noch auf sie zeigt.
   *
   * Ohne das bliebe der Inhalt der gelöschten Prüfung im Store stehen — die Spalten
   * Station, Prüfer und Schauspieler zeigten weiter ihre Zeilen, und `selectStation` liefe
   * gegen Kennungen, die es in der Ablage nicht mehr gibt.
   */
  forgetExam: (examId: string) => void;

  /** Wiedereinstiegsfrage beantwortet: den gemerkten Stand übernehmen. */
  resumeYes: () => void;
  /** Wiedereinstiegsfrage beantwortet: von vorn wählen. Löscht nichts, s. `startup.ts`. */
  resumeNo: () => void;
  /** Auswahl bestätigt. Nur möglich, wenn Prüfung, Station und Prüfer stehen. */
  confirmSetup: () => void;

  openSheet: (sheet: Sheet) => void;
  closeSheet: () => void;

  setSchlaeft: (schlaeft: boolean) => void;
  setMedienAufnahme: (laeuft: boolean) => void;
  setVollbildEditor: (offen: boolean) => void;

  askSwitch: (candidateId: string) => void;
  cancelSwitch: () => void;
  confirmSwitch: () => Promise<void>;
  /** Prüfling aus der Liste des Startdialogs wählen. Ohne Rückfrage — es gibt noch keinen. */
  pickCandidate: (candidateId: string) => Promise<void>;
  setCandidateFilter: (filter: CandidateFilter) => void;
  setCandidateSort: (art: Sortierart) => void;
  setCandidateSearch: (search: string) => void;

  toggleAfterSubmit: (key: keyof AfterSubmit) => void;
  toggleKommentarArt: (art: KommentarArt) => void;
  setzeBogenAnzeige: (schalter: BogenAnzeigeSchalter, an: boolean) => void;
  setzeQuerformat: (an: boolean) => void;
  submitAssessment: (total: number, max: number) => Promise<void>;
  /** Nächster noch nicht bewerteter Prüfling — aus der Liste oder als Rückfall nach dem Scan. */
  goToNextCandidate: () => Promise<void>;
  /**
   * Prüfling anhand eines gescannten Ausweises wählen. Gibt zurück, ob einer gefunden wurde —
   * ein unbekannter Ausweis darf die Auswahl nicht stillschweigend verändern.
   *
   * ⚠️ **Seit der Fix-Runde zu Aufgabe 17 `async`.** Der Rückgabewert war zuvor synchron; das
   * ließ sich nicht mit einem korrekten `await flushNow()` **vor** dem Wechsel vereinbaren
   * (der ausstehende Stand des vorherigen Prüflings muss unter seiner eigenen Kennung
   * geschrieben sein, bevor `candidateId` wechselt — sonst diffed die nächste Sicherung
   * bereits gegen den neuen). Der ursprünglich synchron formulierte Test in
   * `useAppStore.test.ts` ist entsprechend angepasst; die Spec (keine Bewertung darf beim
   * Prüflingswechsel verloren gehen oder falsch zugeordnet werden) ist die bindende Autorität,
   * nicht die Form eines einzelnen Tests.
   */
  selectCandidateByCode: (code: string) => Promise<boolean>;

  toggleChecklistItem: (taskId: TaskId, index: number) => void;
  chooseCriterion: (taskId: TaskId, index: number, option: number) => void;
  clearCriterion: (taskId: TaskId, index: number) => void;
  setOverride: (taskId: TaskId, points: number | null) => void;
  toggleWord: (taskId: TaskId, index: number) => void;

  setCommentText: (taskId: TaskId, text: string) => void;
  addHandwritingStroke: (taskId: TaskId, stroke: InkStroke) => void;
  clearHandwriting: (taskId: TaskId) => void;
  addMedia: (taskId: TaskId, kind: MediaKind, media: CommentMedia) => void;
  removeMedia: (taskId: TaskId, kind: MediaKind, id: string) => void;
}

/** Feldname der Ablage je Medienart. */
const MEDIA_FIELD = { audio: 'audios', photo: 'photos', video: 'videos' } as const;

/** Legt für jede Checklisten-Aufgabe eine Reihe Häkchen an, damit Indizes stets existieren. */
function seedAssessment(stations: Station[]): Assessment {
  const a = emptyAssessment();
  const seen = new Set<TaskId>();
  for (const s of stations) {
    for (const task of s.tasks) {
      if (seen.has(task.id)) continue;
      seen.add(task.id);
      if (task.scoring.kind === 'checklist') {
        a.checked[task.id] = task.scoring.items.map(() => false);
      }
      a.comments[task.id] = emptyComment();
    }
  }
  return a;
}

/** Überträgt den in `CandidateExamMetadata` geführten Zustand auf die Prüflinge einer Prüfung. */
function mitStatus(exam: Exam, statuses: Record<string, RelayStatus>): Exam {
  return {
    ...exam,
    candidates: exam.candidates.map((c) => {
      const status = statuses[c.id];
      return status ? { ...c, state: stateFromStatus(status) } : c;
    }),
  };
}

/**
 * Aufräumer: die Fallvignette stand bis 2026-09-16 wahlweise angeheftet neben dem Bogen, und
 * die Wahl lag unter `ui.vignettePin`. Das Seitenfeld ist weg (Entscheidung des Nutzers), der
 * Eintrag bliebe sonst auf jedem Gerät liegen, das ihn je gesetzt hat — totes Gewicht, das
 * beim nächsten Leser wie eine noch benutzte Einstellung aussähe.
 */
localStorage.removeItem('ui.vignettePin');

/**
 * Was die App-Datenbank über die Darstellung sagt — in den Namen des Katalogs.
 *
 * ⚠️ **Die Umrechnung läuft über `ausRelay`**, nicht über direkte Feldzugriffe: dort steht, wie
 * ein Name der Datenbank auf eine Einstellung fällt, und dort steht auch der Übergang für die
 * alten Servernamen (`ALTE_SERVERNAMEN` in `platform/einstellungen.ts`). Jeder eigene Zugriff
 * hier wäre eine zweite Stelle, die beim nächsten Namenswechsel vergessen wird — genau das ist
 * am 2026-09-17 beinahe passiert.
 *
 * ⚠️ **Zahlen laufen durch dieselben Wachen wie die Setter:** ein von Hand veränderter oder aus
 * einer älteren Fassung stammender Wert darf die Kamera nicht auf 0 oder auf Stunden stellen.
 */
function ausGespeicherten(settings: Record<string, string>) {
  const gelesen = ausRelay(settings);
  const nimm = <T,>(name: Schluessel, wandle: (roh: unknown) => T, sonst: T): T => {
    const roh = gelesen[name];
    return roh === undefined ? sonst : wandle(roh);
  };

  const deviceName = nimm('device_name', (v) => String(v), '');
  setGeraeteName(deviceName);

  return {
    halfPoints: nimm('sheet_half_points', (v) => v !== false && v !== 'false', true),
    timerShow: nimm('sheet_show_timer', (v) => v !== false && v !== 'false', true),
    deviceName,
    videoMaxSeconds: begrenzeVideoDauer(Number(gelesen.video_max_seconds)),
    archivTage: begrenzeArchivTage(Number(gelesen.deleted_files_retention_days)),
  };
}

export const useAppStore = create<AppState>()((set, get) => {
  /** Ändert die Bewertung des gerade gewählten Prüflings. */
  const patchAssessment = (change: (a: Assessment, s: AppState) => Assessment) =>
    set((s) => {
      const current = s.assessments[s.candidateId] ?? seedAssessment(selectExam(s).stations);
      return { assessments: { ...s.assessments, [s.candidateId]: change(current, s) } };
    });

  /** Die Auswahl in der Form, die `Client` erwartet. */
  /**
   * ⚠️ **Leere Kennung heisst `null`, nicht `''`.** Die vier `Client.current*`-Spalten
   * verweisen mit Fremdschlüsseln auf `Exam`, `Candidate`, `Examiner` und `Actor`. Ein leerer
   * String ist kein gültiger Verweis: auf dem Gerät weist SQLite die Zeile ab
   * (`FOREIGN KEY constraint failed`), und weil das Merken nebenher läuft, bliebe die Auswahl
   * dabei still ungespeichert. Seit dem Startablauf sind leere Kennungen der Normalfall —
   * zwischen dem Öffnen der App und dem Ausweisscan ist jede einzelne leer.
   *
   * Am 2026-09-10 auf dem iOS-15-Simulator gemessen, nicht abgeleitet: der IndexedDB-Adapter
   * im Testlauf prüft keine Fremdschlüssel und hätte das nie gezeigt.
   */
  const auswahlVon = (s: AppState) => ({
    id: s.clientId,
    currentExamID: s.examId || null,
    currentCandidateID: s.candidateId || null,
    currentExaminerID: s.examinerId || null,
    currentActorID: s.actorId || null,
  });

  /**
   * Ein Schreibvorgang, auf den die Oberfläche nicht wartet — Protokollzeilen, Zustand,
   * Einstellungen, gemerkte Auswahl. Sie alle laufen bewusst nebenher: ein Häkchen darf nicht
   * auf die Platte warten.
   *
   * ⚠️ Sie liefen bisher als nacktes `void schreiben(…)`, **ohne `catch`**. Scheiterte einer,
   * war die abgelehnte Zusage unbehandelt: im WKWebView ohne Fehlerfläche heißt das spurlos.
   * Ausgerechnet die Zeilen, die den Ablauf der Prüfung festhalten (`CONTAINER_START`/`_END`,
   * `ITEM_VISITED`, der Wechsel auf `RUNNING`), verschwanden damit lautlos. Hier landet
   * wenigstens der Fehlschlag im Dateilog — der zweite Weg zurück, den Spec §9 dafür vorsieht.
   *
   * Bewusst **ohne** Wiederholung und ohne Einblendung: das ist der Weg, den `autosave.ts` für
   * den Bewertungsstand geht, und ihn hierher zu übertragen hiesse, für jede dieser Zeilen zu
   * entscheiden, was ein Fehlschlag für den Prüfer bedeutet. Das gehört zur Abgleichsarbeit von
   * Phase 4, nicht in eine Schlussprüfung.
   */
  const nebenbei = (was: string, versprechen: Promise<unknown>) => {
    void versprechen.catch((fehler) => appendAppLog(was + ' fehlgeschlagen: ' + String(fehler), 'error'));
  };

  /**
   * Eine Einstellung zurück in die iPad-Einstellungen schreiben.
   *
   * **Warum überhaupt** (Auftrag des Nutzers, 2026-09-13): Dort, und nur dort, sieht und setzt
   * eine Geräteverwaltung sie. Ändert der Prüfer etwas in der App, soll die Seite in den
   * iPad-Einstellungen dasselbe zeigen.
   *
   * ⚠️ **Gegen das MDM hilft es nicht.** Steht der Schlüssel dort, gewinnt er beim nächsten
   * Lesen wieder — die Oberfläche sperrt solche Zeilen deshalb (`quellen` im Zustand).
   *
   * ⚠️ **Nebenbei, nie blockierend.** Im Browser gibt es diesen Weg nicht; ein Fehlschlag darf
   * den Prüfer nicht aufhalten.
   */
  const insGeraet = (schluessel: Schluessel, wert: Wert) => {
    // Ist eine Prüfung ausgewählt, gilt die Änderung nur für sie — nicht ins iPad, sonst
    // überlebte sie die Prüfung (Entscheidung des Nutzers, 2026-10-10).
    const { examId, clientId } = get();
    if (examId && JE_PRUEFUNG.has(schluessel)) {
      set((s) => ({ quellen: { ...s.quellen, [schluessel]: 'appPruefung' } }));
      nebenbei(
        'Einstellung ' + schluessel + ' für diese Prüfung merken',
        schreibeAppJePruefung(getStore(), clientId, examId, schluessel, wert),
      );
      return;
    }
    nebenbei(
      'Einstellung ' + schluessel + ' in die iPad-Einstellungen schreiben',
      schreibeEinstellung(schluessel, wert),
    );
  };

  /**
   * Zieht den Bogen ab, so wie er gerade auf dem Schirm steht.
   *
   * ⚠️ **Immer vor dem `set()` rufen.** `currentFileSubject()` und die Beschriftung lesen den
   * *aktuellen* Zustand; liefe der Abzug danach, trüge das Beweisbild die Kennung des
   * Prüflings oder der Station, an der noch gar nichts geschehen ist. Und der Bogen selbst
   * zeigte bereits die neuen Aufgaben — das Bild belegte dann das Gegenteil dessen, wofür es
   * da ist.
   *
   * ⚠️ **Es wird gewartet.** Der Abzug scrollt den Bogen unter der Sperrscheibe durch; ein
   * `set()`, das währenddessen die Aufgabenliste austauscht, ergäbe Kacheln aus zwei
   * verschiedenen Bögen. Der Prüfer wartet dafür rund eine Sekunde je Kachel.
   *
   * `nur wenn berührt`: beim Wechsel entsteht der Abzug nur, wenn an der verlassenen Station
   * überhaupt etwas steht (Vorgabe des Nutzers, 2026-09-28) — ein Durchklicken durch die
   * Prüflingsliste legte sonst reihenweise leere Beweisbilder an. Beim **Abgeben** gilt das
   * nicht: dort zieht es immer ab (Vorgabe des Nutzers, 2026-09-17).
   */
  const abzug = async (grund: Abzugsgrund, nurWennBeruehrt: boolean): Promise<Sheet | null> => {
    const s = get();
    const station = selectStationOrNull(s);
    if (!station || !s.candidateId) return null;
    // Eine Preview ist eine Probe: ihre Abzüge sähen aus wie Beweisbilder einer echten Bewertung.
    if (s.exam?.vorschau) return null;
    if (nurWennBeruehrt && !stationBeruehrt(selectAssessment(s), station)) return null;
    // Ein abgesendeter Bogen ist gesperrt; sein Abgabe-Abzug zeigt denselben Stand schon
    // (Entscheidung des Nutzers, 2026-10-02 — im Gerätelauf entstand jeder Bogen doppelt).
    if (nurWennBeruehrt && s.submitted[s.candidateId]) return null;

    const beschriftung = baueAbzugBeschriftung({
      geraet: s.deviceName,
      zeitpunkt: new Date(),
      station: station.label,
      pruefer: s.exam?.examiners.find((p) => p.id === s.examinerId)?.name ?? '',
      pruefling: s.exam?.candidates.find((c) => c.id === s.candidateId)?.name ?? '',
      grund,
    });

    // Das Blatt zuerst weg: läge der Wechsel- oder Absendedialog noch darüber, zeigte das
    // Beweisbild ihn statt der Bewertung.
    //
    // ⚠️ **Zwei Schalter, nicht einer.** Der Wechseldialog hängt an `switchTarget`, nicht an
    // `sheet` (`App.tsx`). Nur `sheet` zu leeren liess ihn auf dem Bild stehen — im
    // Gerätelauf am 2026-10-02 gesehen. `confirmSwitch` hält sein Ziel lokal, ihm geht dabei
    // nichts verloren.
    //
    // ⚠️ **Die Marke wird zurückgegeben, nicht nur ihr `kind`.** Wer das Blatt danach wieder
    // aufschlagen will (`selectStation`), muss unterscheiden können, ob es *dieser* Abzug
    // weggeräumt hat oder ob inzwischen jemand anders geschlossen hat — im QR-Weg läuft
    // genau das nebenher. Ein `kind === 'none'`-Vergleich verwechselte beide Fälle und
    // schlüge ein Blatt wieder auf, das der Prüfer gerade loswerden wollte.
    const marke = { kind: 'none' } as Sheet;
    set({ sheet: marke, switchTarget: null });
    await captureBogen(
      bogenShotDeps(appendAppLog, {
        zeigen: (text) => set({ shotStamp: text }),
        verbergen: () => set({ shotStamp: null }),
      }),
      currentFileSubject(),
      beschriftung,
    );
    return marke;
  };

  /**
   * Nach jeder Prüflingswahl mit in dasselbe `set()` geben: damit ist der Startablauf durch.
   * Steht er schon auf `bereit`, schreibt es nur den Stand fort — ein Prüflingswechsel
   * mitten in der Station ist derselbe Vorgang wie die erste Wahl.
   */
  const bereitMit = (candidateId: string) => {
    const s = get();
    return {
      startup: {
        kind: 'bereit',
        stand: {
          examId: s.examId,
          stationId: s.stationId,
          candidateId,
          examinerId: s.examinerId,
          actorId: s.actorId,
        },
      } as Startup,
    };
  };

  /**
   * Holt den gesicherten Bogen eines Prüflings, bevor zu ihm gewechselt wird — liegt er nicht
   * im Speicher (nach einem Neustart nur der gemerkte), ginge sonst ein leerer Bogen auf
   * (Audit 2026-10-11, Fund 1). Scheitert das Lesen, bleibt es beim leeren Bogen wie bisher.
   */
  const bogenNachladen = async (candidateId: string) => {
    const s = get();
    if (s.assessments[candidateId] || !s.examId) return;
    try {
      const bogen = await loadAssessment(getStore(), s.taskIndex, s.examId, candidateId, s.stationId || undefined);
      set((alt) => (alt.assessments[candidateId] ? {} : { assessments: { ...alt.assessments, [candidateId]: bogen } }));
    } catch (fehler) {
      await appendAppLog('Bewertung konnte nicht gelesen werden: ' + String(fehler), 'error');
    }
  };

  /** Nach jeder Auswahländerung aufrufen; der Zustandswechsel wartet nicht darauf. */
  const merkeAuswahl = () => {
    nebenbei('Auswahl merken', updateClientSelection(getStore(), auswahlVon(get())));
  };

  /**
   * Ein Prüfling wird an der aktuellen Station aktiv: dieselbe Station beginnt für ihn neu
   * (`CONTAINER_START`, ohne `CONTAINER_END` — der Prüfer bleibt an der Station, nur der
   * Prüfling wechselt), und sein Zustand wird `RUNNING`. Beides ist bestenfalls gesichert;
   * der Zustandswechsel selbst wartet nicht darauf.
   */
  const kandidatBegonnen = (candidateId: string) => {
    const s = get();
    // Ein abgesendeter Bogen beginnt nicht neu: sonst stünde `SUBMITTED` als `RUNNING` in der
    // Ablage, und nach einem Neustart wäre die Sperre fort (2026-11-01).
    if (s.submitted[candidateId]) return;
    nebenbei('CONTAINER_START schreiben', appendLog(getStore(), {
      type: 'CONTAINER_START',
      examID: s.examId,
      candidateID: candidateId,
      examinerID: s.examinerId,
      actorID: s.actorId,
      referenceType: 'EXAM_ITEM_CONTAINER',
      referenceIdentifier: s.stationId,
    }));
    nebenbei('Prüflingszustand auf RUNNING setzen',
      setCandidateStatus(getStore(), s.examId, candidateId, 'RUNNING'));
  };

  return {
    thema: 'system',
    sprache: 'system',
    spracheWaehlbar: true,
    dark: false,
    accent: 'blau',
    halfPoints: true,
    timerShow: true,
    stationComments: true,
    videoMaxSeconds: VIDEO_VORGABE_SEKUNDEN,
    archivTage: ARCHIV_VORGABE_TAGE,
    // Dieselben Werte wie die Vorgaben im Katalog. Sie gelten nur bis `uebernehmeEinstellungen`
    // läuft — das geschieht beim Start, gleich nach dem Hydrieren.
    helligkeitMin: 30,
    helligkeitMax: 80,
    deviceName: '',
    katalogLage: LAGE_STILL,

    exam: null,
    taskIndex: new Map(),
    clientId: '',

    examId: '',
    stationId: '',
    candidateId: '',
    examinerId: '',
    actorId: '',

    assessments: {},

    startup: { kind: 'auswahl' },
    sheet: { kind: 'none' },
    shotStamp: null,
    schlaeft: false,
    medienAufnahme: false,
    vollbildEditor: false,
    switchTarget: null,
    candidateFilter: 'Alle',
    candidateSearch: '',
    candidateSort: 'nachname',

    afterSubmit: { scanner: true, haptics: false },
    // Vorgabeweise sind alle fünf da (Auftrag des Nutzers, 2026-09-18).
    kommentarArten: { text: true, handwriting: true, audio: true, photo: true, video: true },
    bogenAnzeige: VOLLE_ANZEIGE,
    querformat: true,
    autoUpload: true,
    fehlerberichte: true,
    // Dieselben Vorgaben wie im Katalog (`platform/einstellungen.ts`): die Kopie ist aus,
    // die Beschriftung an. Zwei Vorgaben nebeneinander hiesse: je nach Weg gilt etwas anderes.
    fotosKopie: false,
    medienBeschriftung: true,
    videoTon: true,
    // Dieselbe Vorgabe wie im Katalog: Apples öffentlicher Zeitdienst, frei und ohne
    // Anmeldung. Ein Haus trägt seinen eigenen Server ein oder leert das Feld.
    ntpServer: 'time.apple.com',
    rueckstand: LEERER_RUECKSTAND,
    rueckstandSendet: false,
    rueckstandMeldung: null,
    quellen: {},
    einstellungenPasswort: '',
    einstellungenFrei: false,
    beendenPin: '',
    beendenOffen: false,
    submitted: {},

    applyHydration: (result) =>
      set(() => {
        // ⚠️ **Auch ohne Prüfung wird übernommen**, seit die Beispieldaten nur noch auf
        // Knopfdruck kommen (`persistence/hydrate.ts`): ein Gerät ohne Prüfung ist der
        // gewöhnliche Anfangszustand, und der Prüfer muss von dort in die Einstellungen
        // kommen. Früher stand hier `if (!result.exam) return { exam: null }` — dann blieb
        // `clientId` leer, und die Server-Gruppe der Einstellungen (`ServerGroup.tsx`, sie
        // steigt bei leerer `clientId` aus) zeigte weder Zugangsdaten noch den Demo-Knopf.
        const exam = result.exam ? mitStatus(result.exam, result.statuses) : null;
        // ⚠️ **Nichts wird mehr stillschweigend vorgewählt.** Bis zum 2026-09-10 standen hier
        // `stations[0]`, `candidates[0]`, `examiners[0]` und `actors[0]` als Rückfall, und die
        // App öffnete einen fertigen Bogen für einen Prüfling, den niemand gewählt hatte. Jetzt
        // entscheidet `startup`, was als Nächstes zu tun ist; die Kennungen bleiben leer, bis
        // eine Wahl getroffen oder der gemerkte Stand bestätigt ist (s. `startup.ts`).
        // Ohne Prüfung gibt es nichts wiederherzustellen — dann bleibt die Auswahl, leer.
        const start: Startup = exam ? startzustand(result, exam) : { kind: 'auswahl' };
        // ⚠️ Die gelesene Bewertung wird **behalten**, obwohl noch niemand gewählt ist. Sie
        // gehört dem gemerkten Prüfling, und `resumeYes` schaltet nur die Kennung um — läge
        // sie nicht schon hier, zeigte „Weitermachen" einen leeren Bogen. Wird stattdessen
        // neu begonnen, schadet der Eintrag nicht: er ist derselbe Stand, den ein späteres
        // Wählen desselben Prüflings ohnehin aus der Ablage holte.
        const gemerkt = result.client.currentCandidateID ?? '';
        // Der Hinweis „Stand wiederhergestellt" gehört zur Antwort, nicht zur Frage — er
        // wandert nach `resumeYes`.
        return {
          exam,
          taskIndex: result.index,
          clientId: result.client.id,
          startup: start,
          examId: '',
          stationId: '',
          candidateId: '',
          examinerId: '',
          actorId: '',
          assessments: { ...result.boegen, ...(result.assessment && gemerkt ? { [gemerkt]: result.assessment } : {}) },
          submitted: result.abgesendet,
          // Ein gemerktes Blatt darf erst wieder aufgehen, wenn der Startablauf durch ist —
          // sonst kämpfen zwei Mechanismen um denselben Bildschirm. Es wartet so lange im
          // Startzustand (s. `startup.ts`).
          sheet: { kind: 'none' },
          // ⚠️ **Über den Katalog gelesen, nicht über blosse Feldnamen** (2026-09-17). Vorher
          // stand hier `result.settings.halfPoints` — also der alte Name, während der Setter
          // längst `sheet_half_points` schreibt. Der Neustart hätte jede Einstellung
          // verloren. `ausRelay` kennt beide Namen und entscheidet für den neuen.
          ...ausGespeicherten(result.settings),
        };
      }),

    /**
     * Übernimmt, was aus Geräteverwaltung, iPad-Einstellungen und App-Datenbank gemischt
     * wurde (`platform/einstellungen.ts`).
     *
     * ⚠️ **Läuft nach dem Hydrieren**, nicht statt seiner: die App-Datenbank ist dabei die
     * unterste Quelle, das Ergebnis also niemals ärmer. Im Browser, wo es keine
     * iPad-Einstellungen gibt, kommt genau dasselbe heraus, was schon dastand.
     *
     * ⚠️ **Schreibt nichts zurück.** Sonst schriebe jeder Start die gelesenen Werte erneut in
     * die Ablage — und ein vom MDM gesetzter Wert stünde danach als eigene Wahl des Prüfers da.
     */
    uebernehmeEinstellungen: (g) => {
      const quellen: Partial<Record<Schluessel, Quelle>> = {};
      for (const [name, e] of Object.entries(g) as [Schluessel, { quelle: Quelle }][]) {
        quellen[name] = e.quelle;
      }
      setGeraeteName(g.device_name.wert);
      set({
        quellen,
        einstellungenPasswort: g.settings_password.wert,
        beendenPin: g.exit_pin.wert,
        thema: g.ui_theme.wert,
        sprache: g.language.wert,
        spracheWaehlbar: g.language_changeable.wert,
        dark: istDunkel(g.ui_theme.wert),
        accent: g.ui_accent.wert,
        halfPoints: g.sheet_half_points.wert,
        timerShow: g.sheet_show_timer.wert,
        stationComments: g.station_comments_enabled.wert,
        videoMaxSeconds: g.video_max_seconds.wert,
        archivTage: g.deleted_files_retention_days.wert,
        helligkeitMin: g.min_brightness.wert,
        helligkeitMax: g.max_brightness.wert,
        deviceName: g.device_name.wert,
        fotosKopie: g.save_photos_in_album.wert,
        medienBeschriftung: g.media_label.wert,
        videoTon: g.video_audio.wert,
        ntpServer: g.ntp_server.wert,
        afterSubmit: {
          scanner: g.next_candidate_button.wert === 'scan',
          haptics: g.submit_haptics.wert,
        },
        kommentarArten: {
          text: g.text_comments.wert,
          handwriting: g.sketch_comments.wert,
          audio: g.audio_comments.wert,
          photo: g.photo_comments.wert,
          video: g.video_comments.wert,
        },
        // Die sieben Schalter aus dem alten OSCE-Client (Relay-Namen, seit 2026-10-02).
        bogenAnzeige: {
          farbig: g.colorful_text.wert,
          aufgabenpunkte: g.show_task_points.wert,
          summe: g.show_sum_points.wert,
          aenderbar: g.editable_task_points.wert,
          schauspieler: g.actors_enabled.wert,
        },
        querformat: g.landscape_mode.wert,
        autoUpload: g.auto_upload.wert,
        fehlerberichte: g.error_reporting.wert,
        // Ohne Schauspieler gibt es auch keinen gewählten — wie in `setzeBogenAnzeige`.
        ...(g.actors_enabled.wert ? {} : { actorId: '' }),
      });
    },

    setEinstellungenFrei: (einstellungenFrei) => set({ einstellungenFrei }),

    setBeendenOffen: (beendenOffen) => set({ beendenOffen }),

    setDeviceName: (deviceName) => {
      set({ deviceName });
      // Die Ablage darf den Store nicht kennen (s. `platform/appVersion.ts`) — sie bekommt
      // den Namen gemeldet.
      setGeraeteName(deviceName);
      nebenbei('Gerätename sichern', saveSetting(getStore(), get().clientId, relayName('device_name'), deviceName));
      insGeraet('device_name', deviceName);
    },

    /*
     * Seit 2026-10-02 auch aus der App (Entscheidung des Nutzers): im Kiosk-Modus gibt es keinen
     * Weg in die iPad-Einstellungen, und wer das Passwort dort vergessen hat, könnte die App
     * sonst nicht mehr sperren. Wer den Dialog offen hat, hat die Sperre schon hinter sich.
     */
    setEinstellungenPasswort: (einstellungenPasswort) => {
      // Wer das Passwort setzt, hat den Dialog schon offen — er bleibt offen und sagt
      // „gesetzt"; gesperrt wird beim Schliessen (`Einstellungen` in `App.tsx`, 2026-10-02).
      set({ einstellungenPasswort, einstellungenFrei: true });
      nebenbei(
        'Settings-Passwort sichern',
        saveSetting(getStore(), get().clientId, relayName('settings_password'), einstellungenPasswort),
      );
      insGeraet('settings_password', einstellungenPasswort);
    },

    setzeThema: (thema) => {
      // ⚠️ Bis zum 2026-09-13 wurde das dunkle Erscheinungsbild **gar nicht** gesichert — es
      // fiel bei jedem Start zurück. Jetzt trägt es die Einstellungsseite.
      set({ thema, dark: istDunkel(thema) });
      insGeraet('ui_theme', thema);
    },
    /**
     * Das Gerät hat umgeschaltet (`prefers-color-scheme`).
     *
     * ⚠️ **Wirkt nur bei leerer Wahl.** Wer „Hell" oder „Dunkel" gewählt hat, soll nicht
     * mitten in einer Prüfung umspringen, weil auf dem iPad die Nacht anbricht.
     */
    geraetIstDunkel: (dunkel) => {
      if (get().thema !== 'system') return;
      if (get().dark === dunkel) return;
      set({ dark: dunkel });
    },
    setAccent: (accent) => {
      set({ accent });
      nebenbei('Einstellung colorScheme sichern',
        saveSetting(getStore(), get().clientId, relayName('ui_accent'), accent));
      insGeraet('ui_accent', accent);
    },
    toggleHalfPoints: () => {
      set((s) => ({ halfPoints: !s.halfPoints }));
      nebenbei('Einstellung halfPoints sichern',
        saveSetting(getStore(), get().clientId, relayName('sheet_half_points'), String(get().halfPoints)));
      insGeraet('sheet_half_points', get().halfPoints);
    },
    setTimerShow: (timerShow) => {
      set({ timerShow });
      nebenbei('Einstellung showStopWatch sichern',
        saveSetting(getStore(), get().clientId, relayName('sheet_show_timer'), String(timerShow)));
      insGeraet('sheet_show_timer', timerShow);
    },
    setVideoMaxSeconds: (sekunden) => {
      const videoMaxSeconds = begrenzeVideoDauer(sekunden);
      set({ videoMaxSeconds });
      nebenbei('Einstellung videoMaxSeconds sichern',
        saveSetting(getStore(), get().clientId, relayName('video_max_seconds'), String(videoMaxSeconds)));
      insGeraet('video_max_seconds', videoMaxSeconds);
    },
    setArchivTage: (tage) => {
      const archivTage = begrenzeArchivTage(tage);
      set({ archivTage });
      nebenbei('Einstellung archivTage sichern',
        saveSetting(getStore(), get().clientId, relayName('deleted_files_retention_days'), String(archivTage)));
      insGeraet('deleted_files_retention_days', archivTage);
    },
    setzeKatalogLage: (katalogLage) => {
      // Gleiche Lage, gleiches Objekt: sonst zeichnete die Kopfzeile bei jedem Zehn-Sekunden-
      // Blick neu, obwohl sich nichts geändert hat.
      const bisher = get().katalogLage;
      if (
        bisher.netzFehler === katalogLage.netzFehler &&
        bisher.veraltet === katalogLage.veraltet &&
        bisher.verwaist === katalogLage.verwaist
      ) {
        return;
      }
      set({ katalogLage });
    },

    /*
     * ⚠️ **Vier Setter, die es bis zum 2026-09-17 nicht gab.** Diese Einstellungen kamen
     * ausschliesslich aus den iPad-Einstellungen; in der App liessen sie sich nur ansehen.
     * Seit der Einstellungsdialog den Katalog liest, entscheidet dort `inOberflaeche`, ob eine
     * Zeile erscheint — und eine Zeile ohne Setter wäre eine, die beim Tippen nichts tut.
     *
     * Sie schreiben wie alle anderen in die iPad-Einstellungen zurück (`insGeraet`), damit
     * eine Geräteverwaltung denselben Wert sieht. In die App-Datenbank geht nur, was Relay
     * ohnehin führt — diese vier gehören nicht dazu.
     */
    setStationComments: (stationComments) => {
      set({ stationComments });
      insGeraet('station_comments_enabled', stationComments);
    },
    setFotosKopie: (fotosKopie) => {
      set({ fotosKopie });
      insGeraet('save_photos_in_album', fotosKopie);
    },
    setMedienBeschriftung: (medienBeschriftung) => {
      set({ medienBeschriftung });
      insGeraet('media_label', medienBeschriftung);
    },
    setVideoTon: (videoTon) => {
      set({ videoTon });
      insGeraet('video_audio', videoTon);
    },
    /*
     * ⚠️ **Gleiche Zahlen, gleiches Objekt.** Der Takt trägt alle zehn bis dreissig Sekunden
     * denselben Stand ein; ein jedes Mal neues Objekt liesse die Kopfzeile ebenso oft neu
     * zeichnen — mitten in der Bewertung, ohne dass sich etwas geändert hätte. Dieselbe
     * Überlegung wie bei `katalogLage`.
     */
    setzeRueckstand: (zahlen) => {
      const alt = get().rueckstand;
      if (
        alt.pakete === zahlen.pakete &&
        alt.dateien === zahlen.dateien &&
        alt.abgewiesen === zahlen.abgewiesen &&
        DATEIARTEN.every((art) => alt.jeArt[art] === zahlen.jeArt[art])
      ) {
        return;
      }
      set({ rueckstand: zahlen });
    },
    setzeRueckstandSendet: (rueckstandSendet) => {
      if (get().rueckstandSendet !== rueckstandSendet) set({ rueckstandSendet });
    },
    merkeRueckstandsmeldung: (text) => {
      const jetzt = new Date();
      const p = (v: number) => String(v).padStart(2, '0');
      set({
        rueckstandMeldung: { zeit: p(jetzt.getHours()) + ':' + p(jetzt.getMinutes()), text },
      });
    },

    // Ein anderer Prüfungsinhalt heißt andere Stationen, andere Personen, andere Prüflinge —
    // alles Bisherige verlöre seinen Bezug. Anders als beim Hydrieren muss das Lesen hier aus
    // der Ablage nachgeholt werden (nur eine Prüfung ist zu jeder Zeit im Store), deshalb läuft
    // dieser Zweig asynchron; `flushNow()` sichert vorher, was am bisherigen Prüfling noch
    // ungeschrieben war — sonst verlöre der Wechsel selbst Eingaben, die `assessments: {}`
    // gleich darauf verwirft.
    selectExam: (examId) => {
      const s = get();
      if (examId === s.examId) return;
      nebenbei('Prüfung wechseln', (async () => {
        await flushNow();
        // Der letzte Beleg der alten Prüfung: gleich darauf sind Station, Prüfling und
        // Bewertungsstand fort, und nichts davon liesse sich danach noch abziehen.
        await abzug('Prüfungswechsel', true);
        const store = getStore();
        let exam: Exam;
        let index: TaskIndex;
        try {
          exam = await readExam(store, examId);
          index = await buildTaskIndex(store, examId);
        } catch (fehler) {
          // Die Prüfung ist nicht lesbar — die bisherige Auswahl bleibt unangetastet, statt
          // mit einer halb aufgebauten anzutreten. Aber nicht stillschweigend: für den Prüfer
          // sieht ein wirkungsloser Antippen sonst aus wie ein hakendes Gerät.
          await appendAppLog('Prüfung wechseln fehlgeschlagen: ' + String(fehler), 'error');
          return;
        }
        const statuses = await candidateStatus(store, examId);
        const geladen = mitStatus(exam, statuses);
        // Sperre und Bögen der **neuen** Prüfung — `submitted` ist nur nach Prüfling
        // geschlüsselt, derselbe Prüfling kann in beiden Prüfungen stehen.
        const { abgesendet, boegen } = await abgesendeteBoegen(geladen, index, examId);
        // ⚠️ Station, Prüfling, Prüfer und Schauspieler bleiben **leer**. Sie gehören zur
        // gerade verlassenen Prüfung; die erste Zeile der neuen vorzuwählen wäre wieder eine
        // Wahl, die niemand getroffen hat. Der Startablauf führt zurück in die Auswahl.
        set({
          exam: geladen,
          taskIndex: index,
          examId: geladen.id,
          stationId: '',
          candidateId: '',
          examinerId: '',
          actorId: '',
          assessments: boegen,
          submitted: abgesendet,
          startup: { kind: 'auswahl' },
          // Sonst läge ein Blatt der alten Prüfung über dem Auswahldialog.
          sheet: { kind: 'none' },
        });
        // Sofort danach, synchron, bevor am neuen Prüfling überhaupt etwas eingegeben werden
        // kann: der Ausgangsstand des Autospeicherns gehört jetzt ihm. Ohne das träfe der
        // nächste Dämpfer-Zyklus unweigerlich die Prüflings-Wache in `autosave.ts` und
        // verwürfe eine echte erste Eingabe an ihm ungeprüft (Fix-Runde 2 zu Aufgabe 17).
        resetBaseline();
        merkeAuswahl();
      })());
    },
    ladeInhaltNeu: async (examId) => {
      if (get().examId !== examId) return;
      const store = getStore();
      const exam = mitStatus(await readExam(store, examId), await candidateStatus(store, examId));
      const taskIndex = await buildTaskIndex(store, examId);
      const s = get();
      if (s.examId !== examId) return;
      const bleibt = (id: string, liste: { id: string }[]) => (liste.some((e) => e.id === id) ? id : '');
      set({
        exam,
        taskIndex,
        stationId: bleibt(s.stationId, exam.stations),
        candidateId: bleibt(s.candidateId, exam.candidates),
        examinerId: bleibt(s.examinerId, exam.examiners),
        actorId: bleibt(s.actorId, exam.actors),
      });
    },
    selectStation: async (stationId) => {
      const s = get();
      if (stationId === s.stationId) return;
      // ⚠️ Im Startablauf wird die Station gewählt, **bevor** ein Prüfling feststeht. Ein
      // `CONTAINER_END`/`CONTAINER_START` dazu trüge eine leere Prüflings- und Prüfungskennung
      // — genau die Zeilensorte, die die Wache in `autosave.ts` verhindert. Es geht dabei auch
      // nichts verloren: `kandidatBegonnen` schreibt den `CONTAINER_START` ohnehin, sobald der
      // Prüfling gewählt ist, und ein `CONTAINER_END` für eine Station, die niemand betreten
      // hat, wäre schlicht falsch.
      if (!s.candidateId || !s.examId) {
        set({ stationId });
        merkeAuswahl();
        return;
      }
      // ⚠️ **Vor `CONTAINER_END`, und abgewartet.** Der Abzug soll die Station zeigen, die der
      // Prüfer verlässt — mit ihren Aufgaben und ihrem Stand. Nach dem `set()` stünden die
      // Aufgaben der neuen Station auf dem Bild.
      const blatt = s.sheet;
      const marke = await abzug('Stationswechsel', true);
      // Das Auswahlblatt musste für den Abzug weichen (`abzug` räumt es weg, sonst stünde es
      // auf dem Beweisbild). Danach gehört es zurück: der Prüfer hat eine Station gewählt,
      // nicht das Blatt geschlossen, und will von hier aus vielleicht noch den Prüfer
      // wechseln.
      //
      // ⚠️ **Nur, wenn noch genau die Marke des Abzugs liegt.** Lief kein Abzug, ist `marke`
      // leer und das Blatt liegt ohnehin noch da. Hat inzwischen jemand anders geschlossen
      // (der QR-Weg tut das, ohne auf den Abzug zu warten), liegt eine andere Marke — und
      // dann bleibt es geschlossen.
      if (marke && get().sheet === marke) set({ sheet: blatt });

      const grund = {
        examID: s.examId,
        candidateID: s.candidateId,
        examinerID: s.examinerId,
        actorID: s.actorId,
        referenceType: 'EXAM_ITEM_CONTAINER' as const,
      };
      nebenbei('CONTAINER_END schreiben',
        appendLog(getStore(), { ...grund, type: 'CONTAINER_END', referenceIdentifier: s.stationId }));
      nebenbei('CONTAINER_START schreiben',
        appendLog(getStore(), { ...grund, type: 'CONTAINER_START', referenceIdentifier: stationId }));
      set({ stationId });
      merkeAuswahl();
    },
    selectExaminer: (examinerId) => {
      set({ examinerId });
      merkeAuswahl();
    },
    selectActor: (actorId) => {
      // `actors_enabled` aus: auch ein QR-Code setzt keinen Schauspieler — er ginge sonst
      // unsichtbar in jede Protokollzeile.
      if (!get().bogenAnzeige.schauspieler) return;
      set({ actorId });
      merkeAuswahl();
    },

    forgetExam: (examId) => {
      const s = get();
      const betroffen = s.examId === examId || s.exam?.id === examId;
      if (!betroffen) return;
      // Der Bewertungsstand dieser Prüfung geht mit: er zeigte auf Prüflinge, die es auf dem
      // Gerät nicht mehr gibt. In der Ablage ist er ohnehin schon fort (`deleteExam`).
      set({
        exam: null,
        taskIndex: new Map(),
        examId: '',
        stationId: '',
        candidateId: '',
        examinerId: '',
        actorId: '',
        assessments: {},
        startup: { kind: 'auswahl' },
        sheet: { kind: 'none' },
      });
    },

    /**
     * Der gemerkte Stand wird übernommen. Erst hier bekommen die Kennungen im Store einen
     * Wert — bis zur Antwort standen sie im Startzustand und nirgends sonst.
     */
    resumeYes: () => {
      const startup = get().startup;
      if (startup.kind !== 'wiedereinstieg') return;
      const stand = startup.stand;
      set({
        startup: { kind: 'bereit', stand },
        examId: stand.examId,
        stationId: stand.stationId,
        candidateId: stand.candidateId,
        examinerId: stand.examinerId,
        actorId: stand.actorId,
        // Das gemerkte Blatt hat auf diese Antwort gewartet, nicht auf die Frage.
        sheet: startup.sheet,
      });
      useToastStore.getState().show('resumed');
      // Wie an jedem Wechselpfad: der Ausgangsstand der Sicherung gehört jetzt diesem
      // Prüfling, bevor am Bogen überhaupt etwas eingegeben werden kann.
      resetBaseline();
      merkeAuswahl();
    },

    /**
     * Von vorn wählen. ⚠️ **Das löscht nichts.** `ExamLog` ist anhängend; die Bewertung des
     * gemerkten Prüflings bleibt und erscheint wieder, sobald er erneut gewählt wird. „Neu
     * beginnen" heisst „diese Auswahl nicht übernehmen", nicht „diese Bewertung verwerfen".
     */
    resumeNo: () => {
      set({ startup: { kind: 'auswahl' } });
    },

    confirmSetup: () => {
      const s = get();
      if (!s.examId || !s.stationId || !s.examinerId) return;
      set({ startup: { kind: 'pruefling' } });
    },

    openSheet: (sheet) => {
      set({ sheet });
      localStorage.setItem(SHEET_KEY, JSON.stringify(normalizeSheet(sheet)));
      if (sheet.kind === 'comment') {
        const s = get();
        const eintrag = s.taskIndex.get(sheet.taskId);
        // Fehlt die Aufgabe im Index (Prüfung noch nicht importiert), gibt es keine gültige
        // `ExamItem`-Kennung, auf die der Eintrag zeigen könnte — dann bleibt es beim Öffnen.
        if (eintrag) {
          nebenbei('ITEM_VISITED schreiben', appendLog(getStore(), {
            type: 'ITEM_VISITED',
            examID: s.examId,
            candidateID: s.candidateId,
            examinerID: s.examinerId,
            actorID: s.actorId,
            referenceType: 'EXAM_ITEM',
            referenceIdentifier: eintrag.itemId,
          }));
        }
      }
    },
    closeSheet: () => {
      set({ sheet: { kind: 'none' } });
      localStorage.setItem(SHEET_KEY, JSON.stringify(normalizeSheet({ kind: 'none' })));
    },

    setSchlaeft: (schlaeft) => set({ schlaeft }),

    // ⚠️ Setzt die Scheibe **nicht** zurück, wenn eine Aufnahme beginnt. Wer bei liegender
    // Scheibe eine Aufnahme starten könnte, käme ohnehin nicht an die Knöpfe darunter — die
    // Scheibe schluckt jede Berührung.
    setMedienAufnahme: (laeuft) => set({ medienAufnahme: laeuft }),
    setVollbildEditor: (offen) => set({ vollbildEditor: offen }),

    askSwitch: (candidateId) =>
      set((s) =>
        candidateId === s.candidateId
          ? { sheet: { kind: 'none' } as Sheet }
          : { switchTarget: candidateId },
      ),
    cancelSwitch: () => set({ switchTarget: null }),
    confirmSwitch: async () => {
      const target = get().switchTarget;
      if (!target) return;
      // Sichert, was am bisherigen Prüfling noch ungeschrieben war, **bevor** die Auswahl
      // wechselt — sonst läse die nächste (gedämpfte) Sicherung schon den neuen Prüfling und
      // die letzten Eingaben am alten gingen unter, ohne je unter seiner Kennung zu stehen.
      await flushNow();
      await abzug('Prüflingswechsel', true);
      await bogenNachladen(target);
      // Die Uhr gehört zum Prüfling, nicht zur Station: mit dem Wechsel beginnt die
      // Stationszeit des Nächsten (Vorgabe des Nutzers, 2026-09-17).
      useTimerStore.getState().starte();
      set({ candidateId: target, switchTarget: null, sheet: { kind: 'none' }, ...bereitMit(target) });
      // Sofort danach, synchron, bevor am neuen Prüfling überhaupt etwas eingegeben werden
      // kann — s. Kommentar an `resetBaseline()` in `autosave.ts` (Fix-Runde 2 zu Aufgabe 17).
      resetBaseline();
      kandidatBegonnen(target);
      merkeAuswahl();
    },
    /**
     * Die Wahl im Startdialog. Bewusst **ohne** die Rückfrage von `confirmSwitch`: die warnt
     * davor, eine laufende Bewertung zu verlassen, und hier läuft noch keine. Alles Übrige
     * ist dasselbe, damit der über die Liste gewählte Prüfling genauso beginnt wie der
     * gescannte — gleiche Uhr, gleicher `CONTAINER_START`, gleicher Ausgangsstand.
     */
    pickCandidate: async (candidateId) => {
      const exam = selectExam(get());
      if (!exam.candidates.some((c) => c.id === candidateId)) return;
      await flushNow();
      await bogenNachladen(candidateId);
      // Die Wahl des Prüflings **ist** der Anfang der Stationszeit — der Prüfer soll dafür
      // keinen zweiten Knopf suchen müssen, während der Prüfling schon vor ihm steht.
      useTimerStore.getState().starte();
      set({ candidateId, sheet: { kind: 'none' }, ...bereitMit(candidateId) });
      resetBaseline();
      kandidatBegonnen(candidateId);
      merkeAuswahl();
    },

    setCandidateFilter: (candidateFilter) => set({ candidateFilter }),
    setCandidateSort: (candidateSort) => set({ candidateSort }),
    setCandidateSearch: (candidateSearch) => set({ candidateSearch }),

    toggleAfterSubmit: (key) => {
      // Die vier Schalter waren bis zum 2026-09-13 flüchtig: sie standen nach jedem Start
      // wieder auf der Vorgabe. Jetzt tragen sie die iPad-Einstellungen.
      set((s) => ({ afterSubmit: { ...s.afterSubmit, [key]: !s.afterSubmit[key] } }));
      const schluessel = {
        scanner: 'next_candidate_button',
        haptics: 'submit_haptics',
      } as const;
      const wert = get().afterSubmit[key];
      // Seit 2026-10-01 eine Auswahl (Relays `next_candidate_button`), die beiden anderen bleiben Schalter.
      insGeraet(schluessel[key], key === 'scanner' ? (wert ? 'scan' : 'next') : wert);
    },

    setzeBogenAnzeige: (schalter, an) => {
      set((s) => ({ bogenAnzeige: { ...s.bogenAnzeige, [schalter]: an } }));
      // Ohne Schauspieler gibt es auch keinen gewählten: sonst ginge er weiter in jede Zeile.
      if (schalter === 'schauspieler' && !an) set({ actorId: '' });
      // ⚠️ Eine Tabelle, kein zusammengesetzter Name — s. `toggleKommentarArt`.
      const schluessel = {
        farbig: 'colorful_text',
        aufgabenpunkte: 'show_task_points',
        summe: 'show_sum_points',
        aenderbar: 'editable_task_points',
        schauspieler: 'actors_enabled',
      } as const;
      insGeraet(schluessel[schalter], an);
    },

    setzeQuerformat: (querformat) => {
      set({ querformat });
      insGeraet('landscape_mode', querformat);
    },

    toggleKommentarArt: (art) => {
      set((s) => ({ kommentarArten: { ...s.kommentarArten, [art]: !s.kommentarArten[art] } }));
      // ⚠️ Eine Tabelle, kein zusammengesetzter Name. `art + '_comments'` wäre für die
      // Wache (`tools/settings-wache.mjs`) unsichtbar — sie sucht nach Zeichenketten, nicht
      // nach Absichten, und eine Umbenennung im Katalog fiele hier stumm aus.
      const schluessel = {
        text: 'text_comments',
        handwriting: 'sketch_comments',
        audio: 'audio_comments',
        photo: 'photo_comments',
        video: 'video_comments',
      } as const;
      insGeraet(schluessel[art], get().kommentarArten[art]);
    },

    submitAssessment: async (total, max) => {
      const s = get();
      await flushNow();

      // ⚠️ **Hier endet die Stationszeit** (Vorgabe des Nutzers, 2026-09-17): der Prüfer
      // schliesst die Station über diesen Knopf ab, und damit ist sie vorbei. Die Uhr hält an,
      // wo sie steht — sie ist ein Hinweis und beendet von sich aus nichts.
      useTimerStore.getState().stoppe();

      // ⚠️ **Der Abzug zuerst, und zwar mit freiem Blick auf den Bogen.** Der Absende-Dialog
      // liegt in diesem Moment noch darüber; würde jetzt abgezogen, zeigte das Beweisbild den
      // Dialog statt der Bewertung. Also erst schliessen, dann aufnehmen, dann absenden.
      // Auch **vor** `CONTAINER_END` und `SUBMITTED`: das Bild soll den Bogen zeigen, wie der
      // Prüfer ihn abgeschickt hat, nicht wie er danach aussieht.
      const station = selectStation(s);

      /*
       * ⚠️ **Der Abzug entsteht immer** (Vorgabe des Nutzers, 2026-09-17). Er war bis dahin
       * abschaltbar — und war damit genau dort abschaltbar, wo er gebraucht wird: er ist der
       * Beleg, wenn später jemand fragt, was abgegeben wurde. Ein Haus, das ihn ausschaltet,
       * merkt den Verlust erst, wenn es zu spät ist.
       *
       * ⚠️ **Und anders als beim Wechsel ohne Bedingung.** Ein leerer Bogen, der abgegeben
       * wurde, ist genau die Aussage, die später zu belegen ist — deshalb `false`.
       */
      await abzug('Abgabe', false);

      await appendLog(getStore(), {
        type: 'CONTAINER_END',
        examID: s.examId,
        candidateID: s.candidateId,
        examinerID: s.examinerId,
        actorID: s.actorId,
        referenceType: 'EXAM_ITEM_CONTAINER',
        referenceIdentifier: station.id,
      });
      await setCandidateStatus(getStore(), s.examId, s.candidateId, 'SUBMITTED');
      const now = new Date();
      const at =
        String(now.getHours()).padStart(2, '0') + ':' + String(now.getMinutes()).padStart(2, '0');
      set((alt) => ({
        submitted: { ...alt.submitted, [alt.candidateId]: { at, total, max } },
        sheet: { kind: 'locked' } as Sheet,
      }));
    },

    selectCandidateByCode: async (code) => {
      const s = get();
      const exam = selectExam(s);
      const wanted = code.trim().toLowerCase();
      // Der Ausweis trägt die Matrikelnummer — sie ist der verlässliche Weg. Die `id` ist
      // seit Aufgabe 10 die Relay-Kennung und steht auf keinem Ausweis.
      let found = exam.candidates.find((c) => c.matriculationNumber.toLowerCase() === wanted);

      if (!found) {
        // Ersatzweise über den Namen, damit ein abweichend gedruckter Ausweis nicht gleich
        // als unbekannt gilt. Aber nur, wenn der Name **eindeutig** ist: bei zwei gleichnamigen
        // Prüflingen wäre der erste Treffer geraten, und eine Bewertung am falschen Prüfling
        // ist der teuerste Fehler, den diese App machen kann.
        const byName = exam.candidates.filter((c) => c.name.toLowerCase() === wanted);
        if (byName.length === 1) found = byName[0];
      }

      if (!found) return false;
      // Fix-Runde zu Aufgabe 17: **vor** dem Wechsel sichern, nicht danach. Ohne das löst
      // schon das gleich folgende `set()` über `subscribe(plane)` den Dämpfer erneut aus; die
      // nächste Sicherung läse dann `candidateId` bereits als den neuen Prüfling, während der
      // gemerkte Stand (`zuletzt` in `autosave.ts`) noch zum alten gehört — ihr Unterschied
      // wäre bedeutungslos und der ausstehende Stand des alten Prüflings ginge unter, ohne je
      // unter seiner eigenen Kennung geschrieben zu sein. Die Wache in `autosave.ts` verhindert
      // seit derselben Fix-Runde zusätzlich, dass ein solcher Unterschied überhaupt geschrieben
      // wird — dieses `await` sorgt dafür, dass der Stand vorher *tatsächlich* landet, statt
      // nur nicht falsch zugeordnet zu werden.
      await flushNow();
      // Derselbe Ausweis noch einmal gescannt wechselt niemanden — dann läuft die Uhr weiter,
      // statt die schon laufende Station von vorn zu beginnen. Und es gibt nichts zu belegen:
      // der Abzug hängt am Wechsel, nicht am Scan.
      if (found.id !== s.candidateId) {
        await abzug('Prüflingswechsel', true);
        useTimerStore.getState().starte();
      }
      set({ candidateId: found.id, sheet: { kind: 'none' }, ...bereitMit(found.id) });
      // Sofort danach, synchron, bevor am neuen Prüfling überhaupt etwas eingegeben werden
      // kann: ohne das träfe der nächste Dämpfer-Zyklus die Prüflings-Wache in `schreibe()` —
      // die verwürfe dann nicht nur den (harmlosen) Wechsel, sondern auch die erste, echte
      // Eingabe am neuen Prüfling im selben Fenster (Fix-Runde 2 zu Aufgabe 17; das oben
      // beschriebene `await flushNow()` allein schützt nur den *alten* Prüfling, nicht den
      // Ausgangsstand für den *neuen*).
      resetBaseline();
      kandidatBegonnen(found.id);
      merkeAuswahl();
      return true;
    },

    goToNextCandidate: async () => {
      const s = get();
      const exam = selectExam(s);
      // Dieselbe Regel nennt der Bestätigungsdialog auf seinem Knopf (`domain/naechster.ts`).
      const next = naechsterOffener(exam.candidates, s.submitted, s.candidateId);
      if (!next) {
        set({ sheet: { kind: 'none' } as Sheet });
        return;
      }
      await flushNow();
      // Ist der Nächste der Jetzige (nur noch einer offen), wechselt nichts — und ein
      // Beweisbild eines Wechsels, den es nicht gab, wäre eine falsche Aussage.
      if (next.id !== s.candidateId) await abzug('Prüflingswechsel', true);
      useTimerStore.getState().starte();
      set({ candidateId: next.id, sheet: { kind: 'none' } as Sheet, ...bereitMit(next.id) });
      // Sofort danach, synchron, bevor am neuen Prüfling überhaupt etwas eingegeben werden
      // kann — s. Kommentar an `resetBaseline()` in `autosave.ts` (Fix-Runde 2 zu Aufgabe 17).
      resetBaseline();
      kandidatBegonnen(next.id);
      merkeAuswahl();
    },

    toggleChecklistItem: (taskId, index) =>
      patchAssessment((a) => {
        const row = (a.checked[taskId] ?? []).slice();
        row[index] = !row[index];
        return {
          ...a,
          checked: { ...a.checked, [taskId]: row },
          // Ein Häkchen widerspricht einer von Hand gesetzten Punktzahl — die fällt weg.
          override: { ...a.override, [taskId]: null },
        };
      }),

    chooseCriterion: (taskId, index, option) => {
      patchAssessment((a) => ({
        ...a,
        criteria: { ...a.criteria, [criterionKey(taskId, index)]: option },
        override: { ...a.override, [taskId]: null },
      }));
      set({ sheet: { kind: 'none' } });
    },

    clearCriterion: (taskId, index) => {
      patchAssessment((a) => {
        const criteria = { ...a.criteria };
        delete criteria[criterionKey(taskId, index)];
        return { ...a, criteria };
      });
      set({ sheet: { kind: 'none' } });
    },

    setOverride: (taskId, points) => {
      patchAssessment((a) => ({ ...a, override: { ...a.override, [taskId]: points } }));
      set({ sheet: { kind: 'none' } });
    },

    toggleWord: (taskId, index) =>
      patchAssessment((a) => {
        const key = wordKey(taskId, index);
        const picked = { ...a.pickedWords };
        if (picked[key]) delete picked[key];
        else picked[key] = true;
        return { ...a, pickedWords: picked };
      }),

    setCommentText: (taskId, text) =>
      patchAssessment((a) => ({
        ...a,
        comments: { ...a.comments, [taskId]: { ...(a.comments[taskId] ?? emptyComment()), text } },
      })),

    addHandwritingStroke: (taskId, stroke) =>
      patchAssessment((a) => {
        const box = a.comments[taskId] ?? emptyComment();
        return {
          ...a,
          comments: {
            ...a.comments,
            [taskId]: { ...box, handwriting: [...box.handwriting, stroke] },
          },
        };
      }),

    clearHandwriting: (taskId) =>
      patchAssessment((a) => {
        const box = a.comments[taskId] ?? emptyComment();
        return { ...a, comments: { ...a.comments, [taskId]: { ...box, handwriting: [] } } };
      }),

    addMedia: (taskId, kind, media) =>
      patchAssessment((a) => {
        const box = a.comments[taskId] ?? emptyComment();
        const field = MEDIA_FIELD[kind];
        return {
          ...a,
          comments: { ...a.comments, [taskId]: { ...box, [field]: [...box[field], media] } },
        };
      }),

    removeMedia: (taskId, kind, id) =>
      patchAssessment((a) => {
        const box = a.comments[taskId] ?? emptyComment();
        const field = MEDIA_FIELD[kind];
        return {
          ...a,
          comments: {
            ...a.comments,
            [taskId]: { ...box, [field]: box[field].filter((m) => m.id !== id) },
          },
        };
      }),
  };
});

// — Ableitungen, damit Bauteile nicht jedes Mal suchen müssen —

export function selectExam(s: AppState): Exam {
  if (!s.exam) throw new Error('Noch nicht hydriert — applyHydration fehlt.');
  return s.exam;
}

/**
 * ⚠️ **Kein Rückfall auf `stations[0]`.** Bis zum 2026-09-10 stand hier `?? exam.stations[0]`,
 * und dieselbe Ersatzwahl noch zweimal in `applyHydration` und `selectCandidate`. Sie machte
 * „noch nichts gewählt" unmöglich: der Bogen war immer schon ausgefüllt, mit einer Wahl, die
 * niemand getroffen hatte. Der Startablauf (`startup.ts`) hängt daran, dass eine leere Kennung
 * wirklich leer bleibt.
 */
/**
 * Die gewählte Station — **fordernd**, wie `selectExam`.
 *
 * Kein Rückfall auf `stations[0]` mehr: bis zum 2026-09-10 stand hier `?? exam.stations[0]`,
 * und dieselbe Ersatzwahl noch zweimal in `applyHydration` und `selectCandidate`. Sie machte
 * „noch nichts gewählt" unmöglich — der Bogen war immer schon ausgefüllt, mit einer Wahl, die
 * niemand getroffen hatte.
 *
 * Fordernd und nicht `null`-wertig, weil Kopfzeile und Bogen erst gerendert werden, wenn die
 * Station steht (`App.tsx` schaltet danach). Wer vorher etwas anzeigen will, nimmt
 * `selectStationOrNull`.
 */
export function selectStation(s: AppState): Station {
  const exam = selectExam(s);
  const station = s.stationId ? exam.stations.find((st) => st.id === s.stationId) : undefined;
  if (!station) throw new Error('Keine Station gewählt — der Startablauf ist nicht durch.');
  return station;
}

/** Für den Startablauf selbst, der ja gerade prüft, ob schon etwas gewählt ist. */
export function selectStationOrNull(s: AppState): Station | null {
  if (!s.exam || !s.stationId) return null;
  return s.exam.stations.find((st) => st.id === s.stationId) ?? null;
}

export const selectTasks = (s: AppState): Task[] => selectStationOrNull(s)?.tasks ?? [];

/**
 * Der gewählte Prüfling, oder `null`.
 *
 * ⚠️ Anders als die Station ist der Prüfling **wirklich `null`-wertig**: zwischen der Auswahl
 * von Station und Prüfer und dem Scannen des Ausweises zeigt die App bereits den Bogen, aber
 * es steht noch niemand davor. Die Kopfzeile muss das darstellen können.
 */
export function selectCandidate(s: AppState): Candidate | null {
  if (!s.exam || !s.candidateId) return null;
  return s.exam.candidates.find((c) => c.id === s.candidateId) ?? null;
}

/** Wo ohne Prüfling gar nichts anzuzeigen wäre — Absenden, Sperrblatt, Wechsel. */
export function requireCandidate(s: AppState): Candidate {
  const candidate = selectCandidate(s);
  if (!candidate) throw new Error('Kein Prüfling gewählt — der Startablauf ist nicht durch.');
  return candidate;
}

/**
 * Bewertung des gewählten Prüflings. Fehlt sie noch, entsteht sie hier frisch — und zwar
 * stabil, damit React nicht bei jedem Lesen ein neues Objekt sieht.
 */
const seeded = new Map<string, Assessment>();

/**
 * Der leere Bogen, solange keine Prüfung liegt — **ein** Stück, nicht je Aufruf ein neues.
 *
 * ⚠️ **Warum das zählt.** Zustand entscheidet „geändert" an der **Objektidentität**. Gab
 * `selectAssessment` hier jedes Mal ein frisches `emptyAssessment()` zurück, sah jedes Zeichnen
 * wie eine Änderung aus: die Anzeige wurde neu gezeichnet, rief wieder, sah wieder eine
 * Änderung — React bricht das nach einigen hundert Runden mit „Maximum update depth exceeded"
 * ab. In der laufenden App fiel es nicht auf, weil die Blätter, die diesen Selektor abonnieren,
 * nur über einen geladenen Bogen aufgehen; im Rendering-Test der Kommentarreiter schnappte es
 * am 2026-09-18 sofort zu.
 *
 * Die Funktion darunter beschreibt dieselbe Falle für `currentFileSubject` — dort ist der Weg,
 * sie **nicht** zu abonnieren. Hier war das keine Wahl: der Selektor gehört an ein Abonnement.
 *
 * ⚠️ **Eingefroren, und zwar in der Tiefe.** Ein geteiltes Objekt, das jemand beschreibt, wäre
 * schlimmer als das Problem, das es löst — der leere Bogen trüge dann Reste eines früheren
 * Prüflings. Im Produktionscode beschreibt niemand einen gelesenen Bogen (die Setter bauen neue
 * Objekte); der Eisblock sorgt dafür, dass ein künftiger Versuch **wirft** statt still zu
 * wirken: Module laufen im strikten Modus, und dort ist ein Schreibzugriff auf ein gefrorenes
 * Feld ein `TypeError`.
 */
function tiefFrieren<T>(wert: T): T {
  if (wert && typeof wert === 'object') {
    for (const inneres of Object.values(wert)) tiefFrieren(inneres);
    Object.freeze(wert);
  }
  return wert;
}

const OHNE_PRUEFUNG: Assessment = tiefFrieren(emptyAssessment());
export function selectAssessment(s: AppState): Assessment {
  const existing = s.assessments[s.candidateId];
  if (existing) return existing;
  // ⚠️ **Ohne Prüfung ein leerer Bogen, kein Wurf.** Anders als `selectExam` wird dies auch
  // dann gelesen, wenn noch gar nichts gewählt ist: `startAutosave` (`main.tsx`) fragt im
  // Takt nach dem aktuellen Stand, und seit die App auch ohne Prüfung hochkommt, läuft es
  // auf einem frisch bespielten iPad vom ersten Augenblick an. Am 2026-09-12 im Browser
  // gefunden — die App blieb weiss, bevor überhaupt etwas zu sehen war. Gesichert wird von
  // einem leeren Bogen nichts: `autosave.ts` steigt ohne gewählten Prüfling vorher aus.
  if (!s.exam) return OHNE_PRUEFUNG;
  const key = s.examId + '/' + s.candidateId;
  let blank = seeded.get(key);
  if (!blank) {
    blank = seedAssessment(selectExam(s).stations);
    seeded.set(key, blank);
  }
  return blank;
}

/**
 * Die Zuordnung, die in jeden Dateinamen wandert — aus der aktuellen Auswahl, damit eine
 * abgelegte Datei ohne Datenbank noch dem richtigen Prüfling zuzuordnen ist.
 *
 * ⚠️ **Kein Selektor für `useAppStore(...)`.** Die Funktion baut jedes Mal ein neues Objekt;
 * als Abonnement genutzt, hielte Zustand das für eine Änderung und React liefe in eine
 * Endlosschleife („getSnapshot should be cached"). Sie wird deshalb mit
 * `useAppStore.getState()` im Moment des Auslösens gerufen — reaktiv muss sie nicht sein.
 */
export function currentFileSubject() {
  const s = useAppStore.getState();
  return {
    examId: s.examId,
    // Die Station steht seit dem 2026-09-14 auch im **Dateinamen**, nicht mehr nur im Manifest
    // der Beglaubigung — der Server liest die Zuordnung allein aus dem Namen, seit der `value`
    // der Protokollzeile nur noch die Anzahl trägt (`domain/medienzahl.ts`).
    stationId: s.stationId,
    candidateId: s.candidateId,
    examinerId: s.examinerId,
    // ⚠️ Der Schauspieler fehlte bis zum 2026-09-14 ganz. Er steht in jeder Protokollzeile
    // (`actorID`), stand aber in keinem Dateinamen — eine Aufnahme liess sich ihm also nicht
    // zuordnen.
    actorId: s.actorId,
  };
}

/**
 * Zugang zum Store für die Entwicklung.
 *
 * Ohne das ist der Zustand der laufenden App von außen nicht erreichbar: ein dynamischer
 * `import()` aus der Browser-Konsole liefert eine **andere** Modulinstanz und damit einen
 * zweiten, leeren Store — ein Test darauf beweist nichts.
 *
 * Steht nur im Entwicklungsbau; `import.meta.env.DEV` ist im Auslieferungsbau `false`, die
 * Zuweisung fällt beim Bauen weg. Der zusätzliche `window`-Schutz ist neu seit dieser Aufgabe:
 * `useAppStore.ts` bekommt mit `useAppStore.test.ts` zum ersten Mal einen Test, der es direkt
 * importiert — unter Vitest (`environment: 'node'`, s. `vitest.config.ts`) ist `DEV` weiterhin
 * `true`, aber es gibt kein `window`; ohne die Prüfung risse das jeden Testlauf mit.
 */
if (import.meta.env.DEV && typeof window !== 'undefined') {
  (window as unknown as { osceStore?: typeof useAppStore }).osceStore = useAppStore;
}

/**
 * Warten, bis `selectExam` die Prüfung im Store hat — sie wird asynchron nachgelesen.
 *
 * Höchstens zwei Sekunden: danach ist entweder etwas schiefgegangen, oder das Gerät ist so
 * langsam, dass eine Meldung ehrlicher ist als weiteres Warten.
 */
export async function warteAufExam(examId: string) {
  for (let versuch = 0; versuch < 20; versuch += 1) {
    const exam = useAppStore.getState().exam;
    if (exam && exam.id === examId) return exam;
    await new Promise((fertig) => setTimeout(fertig, 100));
  }
  return null;
}
